import React, { useState, useContext } from "react";
import Ctx from "../../contexts/AppContext";
import { crd, rad, sp } from "../../constants/styles";
import { gradCardStyle, gradLift, glassPill, glassPanel } from "../../constants/gradCard";
import ReceiptView from "../landlord/ReceiptView";
import { formatMonthLabel, monthSortValue, rentPeriod } from "../../utils/dateFormat";
import { downloadCsv, downloadPdf } from "../../utils/exportTable";

function TenPayHist() {
  const { t, h, pays, props, tens, currUserId, go } = useContext(Ctx);
  const ct = currUserId;
  const mp = pays.filter(x => x.tid === ct);
  const allTxns = mp.flatMap(p => (p.txns || []).map(tx => ({ ...tx, parentMo: p.mo })));
  const [receiptPay, setReceiptPay] = useState(null);
  const [tab, setTab] = useState("summary"); // "summary" | "txns"
  // Phase: transaction-range filter. "1m" / "6m" / "12m" / "custom".
  // Default to last 6 months — covers most "show me what I've paid
  // recently" intent without truncating typical lease histories.
  const [txnsRange, setTxnsRange] = useState("6m");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  // Phase: render mode for the txns tab. "cards" is the rich/visual
  // layout; "list" is a compact spreadsheet-style table for users who
  // want to scan many rows at once (e.g. reconciling against a bank
  // statement). Default to "cards" to preserve the existing UX.
  const [txnsView, setTxnsView] = useState("cards");
  // Phase: list-view sort. Each column header is clickable; clicking
  // the active column toggles direction. Default to date-desc — newest
  // payment first, which matches what most users want when scanning
  // recent activity (bank-statement style).
  const [sortKey, setSortKey] = useState("dt");
  const [sortDir, setSortDir] = useState("desc");
  const me = tens.find(x => x.id === ct);
  const myProp = props.find(x => x.id === me?.pid);

  if (receiptPay) return <ReceiptView pay={receiptPay} prop={myProp} tenant={me} onBack={() => setReceiptPay(null)} />;

  const paidTotal = mp.filter(p => p.st === "paid").reduce((s, p) => s + p.amt + (p.com || 0), 0);
  const advanceTotal = mp.reduce((s, p) => s + (p.advance || 0), 0);
  // Subtract only the PENDING AMOUNT from rows with a pending submission
  // (matching TenDash) — excluding those rows entirely made the Due tile
  // here read ₹0 while the dashboard still showed the unpaid remainder.
  const dueGross = mp.filter(p => p.st === "due" || p.st === "partial").reduce((s, p) => {
    const gross = p.amt + (p.com || 0);
    const txnPaid = (p.txns || []).reduce((sum, tx) => sum + (tx.status === "verified" ? tx.amt : 0), 0);
    const pendingAmt = p.pending?.amt || 0;
    return s + Math.max(0, gross - txnPaid - pendingAmt);
  }, 0);
  // Net the available advance against gross due — same logic as TenDash so
  // the two screens never disagree about what the tenant actually owes.
  // The advance tile keeps showing the available balance so the user
  // understands what's about to be consumed.
  const appliedAdvance = Math.min(advanceTotal, dueGross);
  const dueTotal = Math.max(0, dueGross - appliedAdvance);
  const pendingCount = mp.filter(p => p.pending).length;

  return <div style={{ paddingTop: 16 }}>
    <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, marginBottom: 12 }}>{t.myPay}</h2>

    {/* Summary strip */}
    <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: "45%", ...gradCardStyle("green"), borderRadius: rad.md, padding: "10px 12px" }}>
        <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px" }}>{t.paid}</div>
        <div style={{ color: "#fff", fontWeight: 700, fontSize: 15, marginTop: 2 }}>₹{paidTotal.toLocaleString()}</div>
      </div>
      <div style={{ flex: 1, minWidth: "45%", ...gradCardStyle(dueTotal > 0 ? "red" : "green"), borderRadius: rad.md, padding: "10px 12px" }}>
        <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px" }}>{t.due}</div>
        <div style={{ color: "#fff", fontWeight: 700, fontSize: 15, marginTop: 2 }}>₹{dueTotal.toLocaleString()}{pendingCount > 0 ? <span style={{ fontSize: 10, color: "rgba(255,255,255,0.9)", fontWeight: 500 }}> +{pendingCount} ⏳</span> : null}</div>
        {appliedAdvance > 0 && (
          <div style={{ color: "rgba(255,255,255,0.9)", fontSize: 9, fontWeight: 500, marginTop: 2 }}>
            after ₹{appliedAdvance.toLocaleString()} advance
          </div>
        )}
      </div>
      {advanceTotal > 0 && <div style={{ flex: 1, minWidth: "45%", background: `${h.ac}10`, border: `1px solid ${h.ac}20`, borderRadius: rad.md, padding: "10px 12px" }}>
        <div style={{ color: h.ts, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px" }}>Advance</div>
        <div style={{ color: h.ac, fontWeight: 700, fontSize: 15, marginTop: 2 }}>₹{advanceTotal.toLocaleString()}</div>
      </div>}
    </div>

    {/* Tabs */}
    <div style={{ display: "flex", background: h.ib, borderRadius: rad.md, padding: 3, marginBottom: 14 }}>
      {[["summary", t.monthlySummary || "Monthly Summary"], ["txns", `${t.txnHistory || "Transactions"}${allTxns.length > 0 ? ` (${allTxns.length})` : ""}`]].map(([id, label]) => (
        <button key={id} onClick={() => setTab(id)} style={{
          flex: 1, padding: "8px 4px", borderRadius: rad.sm + 2, border: "none",
          background: tab === id ? h.cd : "transparent",
          color: tab === id ? h.ac : h.ts,
          fontSize: 12, fontWeight: tab === id ? 700 : 500,
          cursor: "pointer", transition: "all 0.2s",
          boxShadow: tab === id ? h.sh : "none",
        }}>{label}</button>
      ))}
    </div>

    {/* Monthly Summary tab */}
    {tab === "summary" && <>
      {mp.length === 0 && <p style={{ color: h.sc, textAlign: "center", padding: 24 }}>{t.noData}</p>}
      {/* Newest-first by month tag (see monthSortValue — it parses the
          "MMM YY" format the old Date()-based sort got wrong, which left
          the current month stranded in the middle of the list). */}
      {mp.slice().sort((a, b) => monthSortValue(b.mo) - monthSortValue(a.mo)).map(p => {
        const gross = p.amt + (p.com || 0);
        const txnPaid = (p.txns || []).reduce((s, tx) => s + (tx.status === "verified" ? tx.amt : 0), 0);
        const remaining = Math.max(0, gross - txnPaid);
        const isPartial = p.st === "partial" || (txnPaid > 0 && txnPaid < gross && p.st !== "paid");
        const hasAdvance = (p.advance || 0) > 0;
        const isAdvanceOnly = gross === 0 && hasAdvance;
        const displayAmt = isAdvanceOnly ? p.advance : isPartial ? remaining : gross;
        const tone = isAdvanceOnly ? "indigo" : p.st === "paid" ? "green" : isPartial ? "amber" : p.pending ? "amber" : "red";
        const stLabel = isAdvanceOnly ? `💰 Advance`
          : p.st === "paid" ? (hasAdvance ? `✓ Paid + Advance` : "✓ " + t.paid)
          : isPartial ? `⚡ Partial — ₹${remaining.toLocaleString()} due`
          : p.pending ? "⏳ " + t.pending : t.due;
        // Card is tappable only when there's actually something to pay —
        // `due` or `partial` rows that don't already have a pending tenant
        // submission. Tapping routes to TenPayNow which handles the
        // advance-application + payment flow.
        const isPayable = (p.st === "due" || p.st === "partial") && !p.pending && !isAdvanceOnly;
        return <div
          key={p.id}
          onClick={isPayable ? () => go("tenPay") : undefined}
          onMouseOver={(e) => gradLift(e, tone, true)}
          onMouseOut={(e) => gradLift(e, tone, false)}
          style={{
            ...crd(h),
            ...gradCardStyle(tone),
            cursor: isPayable ? "pointer" : "default",
          }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>{isAdvanceOnly ? "💰 Advance Payment" : formatMonthLabel(p.mo)}</div>
              {/* Subtitle: only show date / method when at least one is set.
                  An unpaid row with neither used to render "— · —" which
                  reads as broken UI. Now we either show the real metadata
                  or a friendly status hint. */}
              {(p.dt || p.mth) ? (
                <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 11, marginTop: 2 }}>
                  {p.st === "paid" && p.dt ? `${t.paidOn || "Paid on"} ${p.dt}` : (p.dt || "—")}{p.mth ? ` · ${p.mth.toUpperCase()}` : ""}
                </div>
              ) : (
                <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 11, marginTop: 2, fontStyle: "italic" }}>
                  {p.pending ? "Awaiting approval" : "Not yet paid"}
                </div>
              )}
              {/* Which stay this payment covers (arrears cycle) — mirrors
                  the landlord PayPage card. */}
              {!isAdvanceOnly && rentPeriod(p.mo, me?.rent_due_day) && (
                <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 10, marginTop: 1, display: "flex", alignItems: "center", gap: 3 }}>
                  <span>{"🗓️"}</span>{rentPeriod(p.mo, me?.rent_due_day)} {t.rentAmt || "Rent"}
                </div>
              )}
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>₹{displayAmt.toLocaleString()}</div>
              <span style={glassPill}>{stLabel}</span>
              {isPayable && (
                <div style={{ color: "#fff", fontSize: 10, fontWeight: 600, marginTop: 4, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 3 }}>
                  {t.payNow || "Pay Now"} <span aria-hidden="true">›</span>
                </div>
              )}
            </div>
          </div>
          {p.com > 0 && <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 10, marginTop: 4 }}>{t.rentAmt} ₹{p.amt.toLocaleString()} + {t.commonAmt} ₹{p.com}</div>}
          {isPartial && txnPaid > 0 && <div style={{ ...glassPanel(h), display: "flex", alignItems: "center", gap: 6, marginTop: 6, padding: "6px 10px", borderRadius: 8 }}>
            <span style={{ fontSize: 12 }}>✅</span>
            <span style={{ color: "#16a34a", fontSize: 11, fontWeight: 600 }}>Paid: ₹{txnPaid.toLocaleString()}</span>
            <span style={{ color: h.sc, fontSize: 10, marginLeft: "auto" }}>of ₹{gross.toLocaleString()}</span>
          </div>}
          {hasAdvance && <div style={{ ...glassPanel(h), display: "flex", alignItems: "center", gap: 6, marginTop: 6, padding: "6px 10px", borderRadius: 8 }}>
            <span style={{ fontSize: 12 }}>💰</span>
            <span style={{ color: h.ac, fontSize: 11, fontWeight: 600 }}>Advance: ₹{p.advance.toLocaleString()}</span>
          </div>}
          {p.st === "paid" && <button onClick={() => setReceiptPay(p)} style={{ marginTop: 10, width: "100%", padding: "10px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.92)", color: h.ac, cursor: "pointer", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>{"🧾 " + t.receiptDownShare}</button>}
        </div>;
      })}
    </>}

    {/* Transactions tab */}
    {tab === "txns" && (() => {
      // Range-filter the txn list. We compare on the tenant-supplied
      // `tx.dt` (the date they actually paid). If parsing fails for any
      // reason we *keep* the row — silently dropping a payment because
      // of a date-string quirk is worse than showing one extra row.
      const now = new Date();
      const startOf = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
      const monthsAgo = (n) => { const x = new Date(now); x.setMonth(x.getMonth() - n); x.setHours(0,0,0,0); return x; };
      const parseDt = (s) => {
        if (!s) return null;
        const d = new Date(s);
        return isNaN(d.getTime()) ? null : d;
      };
      const inRange = (tx) => {
        const d = parseDt(tx.dt);
        if (!d) return true; // keep undated rows visible
        if (txnsRange === "1m") return d >= monthsAgo(1);
        if (txnsRange === "6m") return d >= monthsAgo(6);
        if (txnsRange === "12m") return d >= monthsAgo(12);
        if (txnsRange === "custom") {
          const from = customFrom ? startOf(customFrom) : null;
          const to = customTo ? startOf(customTo) : null;
          if (from && d < from) return false;
          if (to) { const toEnd = new Date(to); toEnd.setHours(23,59,59,999); if (d > toEnd) return false; }
          return true;
        }
        return true;
      };
      const visibleTxns = allTxns.filter(inRange);
      const pills = [
        ["1m", "Last month"],
        ["6m", "Last 6 months"],
        ["12m", "Last 12 months"],
        ["custom", "Custom"],
      ];
      return <>
        {/* View toggle — Cards (rich, visual) vs List (compact table for
            scanning many rows). Right-aligned so it doesn't compete with
            the range pills for primary attention. */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <div style={{ display: "inline-flex", background: h.ib, borderRadius: rad.sm + 2, padding: 2 }}>
            {[
              ["cards", "▦", "Cards"],
              ["list", "≡", "List"],
            ].map(([id, icon, label]) => {
              const active = txnsView === id;
              return <button
                key={id}
                onClick={() => setTxnsView(id)}
                title={`${label} view`}
                aria-label={`${label} view`}
                aria-pressed={active}
                style={{
                  padding: "4px 10px",
                  borderRadius: rad.sm,
                  border: "none",
                  background: active ? h.cd : "transparent",
                  color: active ? h.ac : h.ts,
                  fontSize: 12,
                  fontWeight: active ? 700 : 500,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  boxShadow: active ? h.sh : "none",
                  transition: "all 0.15s",
                }}>
                <span aria-hidden="true" style={{ fontSize: 13 }}>{icon}</span>
                {label}
              </button>;
            })}
          </div>
        </div>

        {/* Range filter pills — single-row, horizontally scrollable on
            narrow viewports so the four options never wrap awkwardly. */}
        <div style={{ display: "flex", gap: 6, marginBottom: 10, overflowX: "auto", paddingBottom: 2, WebkitOverflowScrolling: "touch" }}>
          {pills.map(([id, label]) => {
            const active = txnsRange === id;
            return <button
              key={id}
              onClick={() => setTxnsRange(id)}
              style={{
                flexShrink: 0,
                padding: "6px 12px",
                borderRadius: 999,
                border: `1px solid ${active ? h.ac : h.bd || h.sc + "30"}`,
                background: active ? h.ac : "transparent",
                color: active ? "#fff" : h.tx,
                fontSize: 12,
                fontWeight: active ? 700 : 500,
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "all 0.15s",
              }}>{label}</button>;
          })}
        </div>

        {/* Custom date pickers — only mounted when the user picks
            "Custom" so they don't take vertical space in the common case. */}
        {txnsRange === "custom" && (
          <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ color: h.sc, fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
              From
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                style={{ padding: "4px 8px", borderRadius: 8, border: `1px solid ${h.bd || h.sc + "30"}`, background: h.cd, color: h.tx, fontSize: 12, colorScheme: h.dk ? "dark" : "light" }} />
            </label>
            <label style={{ color: h.sc, fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
              To
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                style={{ padding: "4px 8px", borderRadius: 8, border: `1px solid ${h.bd || h.sc + "30"}`, background: h.cd, color: h.tx, fontSize: 12, colorScheme: h.dk ? "dark" : "light" }} />
            </label>
          </div>
        )}

        {/* Count line — confirms the filter is actually doing something
            and tells the user how much was hidden. */}
        {allTxns.length > 0 && (
          <div style={{ color: h.sc, fontSize: 11, marginBottom: 8 }}>
            Showing {visibleTxns.length} of {allTxns.length} {allTxns.length === 1 ? "transaction" : "transactions"}
          </div>
        )}

        {allTxns.length === 0 && <p style={{ color: h.sc, textAlign: "center", padding: 24 }}>{t.noData}</p>}
        {allTxns.length > 0 && visibleTxns.length === 0 && (
          <p style={{ color: h.sc, textAlign: "center", padding: 24, fontSize: 13 }}>
            No transactions in this range. Try a wider window.
          </p>
        )}
        {txnsView === "cards" && visibleTxns.slice().sort((a, b) => {
          // Newest-first by payment date. Falls back to `parentMo`
          // (the rent month the txn belongs to) when `dt` is missing
          // so undated rows don't all collapse to the bottom in random
          // order. Anything still unparseable is treated as oldest.
          const dv = (s) => { if (!s) return -Infinity; const d = new Date(s); return isNaN(d.getTime()) ? -Infinity : d.getTime(); };
          // parentMo is a "MMM YY" tag — new Date() misparses it (year read
          // as a day → 2001), so convert via monthSortValue instead.
          const mv = (mo) => { const v = monthSortValue(mo); return v === -Infinity ? -Infinity : new Date(Math.floor(v / 12), v % 12, 1).getTime(); };
          const va = dv(a.dt) !== -Infinity ? dv(a.dt) : mv(a.parentMo);
          const vb = dv(b.dt) !== -Infinity ? dv(b.dt) : mv(b.parentMo);
          return vb - va;
        }).map((tx, i) => {
        const stLabel = { approved: "✓ " + t.approved, verified: "✓ Verified", pending: "⏳ " + t.pending, rejected: "✕ " + t.rejected };
        const tone = (tx.status === "verified" || tx.status === "approved") ? "green" : tx.status === "rejected" ? "red" : "amber";
        return <div key={tx.id || i}
          onMouseOver={(e) => gradLift(e, tone, true)}
          onMouseOut={(e) => gradLift(e, tone, false)}
          style={{ ...crd(h), ...gradCardStyle(tone) }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>₹{tx.amt.toLocaleString()}</div>
              <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 11 }}>{tx.parentMo}</div>
            </div>
            <span style={glassPill}>{stLabel[tx.status] || tx.status}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
            <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 11 }}>{t.method + ":"} <span style={{ color: "#fff", fontWeight: 500 }}>{(tx.mth || "—").toUpperCase()}</span></div>
            <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 11 }}>{t.date + ":"} <span style={{ color: "#fff", fontWeight: 500 }}>{tx.dt}</span></div>
            <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 11 }}>{t.type + ":"} <span style={{ color: "#fff", fontWeight: 600 }}>{tx.type === "full" ? t.full : (tx.type || "").startsWith("advance") ? (t.advancePay || "Advance") : t.partial}</span></div>
            {tx.status === "approved" && tx.remaining > 0 && <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 11 }}>{t.remaining + ":"} <span style={{ color: "#fff", fontWeight: 600 }}>₹{tx.remaining.toLocaleString()}</span></div>}
            {tx.status === "approved" && tx.approvedAt && <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 11 }}>{t.approvedOn + ":"} <span style={{ color: "#fff" }}>{tx.approvedAt}</span></div>}
            {tx.status === "rejected" && <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 11 }}>{t.rejectedOn + ":"} <span style={{ color: "#fff" }}>{tx.rejectedAt}</span></div>}
          </div>
          {tx.totalWas > 0 && <div style={{ marginTop: 6, color: "rgba(255,255,255,0.8)", fontSize: 10 }}>{t.totalWas} ₹{tx.totalWas.toLocaleString()}</div>}
        </div>;
      })}

      {/* Spreadsheet-style list view — compact table that scrolls
          horizontally on narrow phones (sub-360px). Same data, denser
          presentation. Zebra striping makes row-tracking easy. */}
      {txnsView === "list" && visibleTxns.length > 0 && (() => {
        const stColor = { approved: h.gn, verified: h.gn, pending: h.wn, rejected: h.rd };
        const stShort = { approved: "Apprvd", verified: "Verifd", pending: "Pend", rejected: "Rejctd" };
        const typeShort = { full: "Full", partial: "Part", advance: "Adv", "advance-applied": "Adv" };
        // Sort comparator. For date-ish columns we parse to a number so
        // "2026-03-27" and "Mar 26" both sort chronologically rather than
        // lexically (which would put "Apr" before "Mar"). For text we
        // do a case-insensitive locale compare.
        const dateVal = (s) => { if (!s) return -Infinity; const d = new Date(s); return isNaN(d.getTime()) ? -Infinity : d.getTime(); };
        const keyVal = (tx) => {
          switch (sortKey) {
            case "dt": return dateVal(tx.dt);
            case "amt": return tx.amt || 0;
            case "mth": return (tx.mth || "").toLowerCase();
            case "type": return (tx.type || "").toLowerCase();
            case "status": return (tx.status || "").toLowerCase();
            case "parentMo": return monthSortValue(tx.parentMo); // "MMM YY" — new Date() misparses it
            default: return 0;
          }
        };
        const sortedTxns = visibleTxns.slice().sort((a, b) => {
          const va = keyVal(a), vb = keyVal(b);
          if (va < vb) return sortDir === "asc" ? -1 : 1;
          if (va > vb) return sortDir === "asc" ? 1 : -1;
          return 0;
        });
        // Click handler — same column toggles direction, different
        // column resets to a sensible default (desc for numeric/date,
        // asc for text so the alphabet starts at A).
        const onSort = (k) => {
          if (sortKey === k) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
          } else {
            setSortKey(k);
            setSortDir(k === "dt" || k === "amt" || k === "parentMo" ? "desc" : "asc");
          }
        };
        const arrow = (k) => sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : " ⇅";
        const thBase = { padding: "8px 8px", color: h.ts, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", borderBottom: `1px solid ${h.bd || h.sc + "20"}`, whiteSpace: "nowrap", position: "sticky", top: 0, background: h.cd, cursor: "pointer", userSelect: "none" };
        const thCell = (label, k, align) => {
          const active = sortKey === k;
          return <th
            onClick={() => onSort(k)}
            title={`Sort by ${label}`}
            aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
            style={{ ...thBase, textAlign: align || "left", color: active ? h.ac : h.ts }}>
            {label}<span style={{ fontSize: 9, opacity: active ? 1 : 0.5 }}>{arrow(k)}</span>
          </th>;
        };
        const td = { padding: "8px 8px", fontSize: 12, color: h.tx, borderBottom: `1px solid ${h.bd || h.sc + "15"}`, whiteSpace: "nowrap" };

        // Export shape — flatten to plain strings so CSV/PDF cells
        // render correctly without React fragments. We export the
        // *current sorted+filtered* set so the file matches what the
        // user is looking at on screen (date range + active sort
        // column both preserved).
        const exportHeaders = [
          { key: "dt", label: "Date" },
          { key: "amt", label: "Amount (₹)" },
          { key: "mth", label: "Method" },
          { key: "type", label: "Type" },
          { key: "status", label: "Status" },
          { key: "parentMo", label: "For Month" },
        ];
        const exportRows = sortedTxns.map(tx => ({
          dt: tx.dt || "",
          amt: tx.amt || 0,
          mth: (tx.mth || "").toUpperCase(),
          type: tx.type || "",
          status: tx.status || "",
          parentMo: tx.parentMo || "",
        }));
        // Filename gets a YYYY-MM-DD stamp so multiple exports don't
        // overwrite each other in the user's Downloads folder.
        const today = new Date().toISOString().slice(0, 10);
        const rangeLabel = txnsRange === "1m" ? "Last 1 month"
          : txnsRange === "6m" ? "Last 6 months"
          : txnsRange === "12m" ? "Last 12 months"
          : txnsRange === "custom" ? `${customFrom || "—"} to ${customTo || "—"}`
          : "All time";
        const exportBtnStyle = {
          padding: "6px 12px",
          borderRadius: rad.sm,
          border: `1px solid ${h.ac}`,
          background: "transparent",
          color: h.ac,
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        };

        return <>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginBottom: 8, flexWrap: "wrap" }}>
          <button
            onClick={() => downloadCsv(exportRows, exportHeaders, `transactions-${today}.csv`)}
            title="Download as CSV (Excel)"
            aria-label="Download as CSV"
            style={exportBtnStyle}>
            <span aria-hidden="true">⤓</span> CSV
          </button>
          <button
            onClick={() => downloadPdf(exportRows, exportHeaders, "My Payment Transactions", rangeLabel)}
            title="Download as PDF (Print)"
            aria-label="Download as PDF"
            style={{ ...exportBtnStyle, background: h.ac, color: "#fff" }}>
            <span aria-hidden="true">⤓</span> PDF
          </button>
        </div>
        <div style={{ ...crd(h), padding: 0, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                {thCell("Date", "dt")}
                {thCell("Amount", "amt", "right")}
                {thCell("Method", "mth")}
                {thCell("Type", "type")}
                {thCell("Status", "status")}
                {thCell("For", "parentMo")}
              </tr>
            </thead>
            <tbody>
              {sortedTxns.map((tx, i) => {
                const sc = stColor[tx.status] || h.sc;
                return <tr key={tx.id || i} style={{ background: i % 2 === 0 ? "transparent" : (h.ib || `${h.sc}08`) }}>
                  <td style={td}>{tx.dt || "—"}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>₹{tx.amt.toLocaleString()}</td>
                  <td style={td}>{(tx.mth || "—").toUpperCase()}</td>
                  <td style={{ ...td, color: tx.type === "full" ? h.gn : tx.type === "advance" ? h.ac : h.wn, fontWeight: 600 }}>{typeShort[tx.type] || tx.type || "—"}</td>
                  <td style={td}>
                    <span style={{ color: sc, fontWeight: 600, fontSize: 11, padding: "2px 6px", borderRadius: 6, background: `${sc}15`, whiteSpace: "nowrap" }}>
                      {stShort[tx.status] || tx.status}
                    </span>
                  </td>
                  <td style={{ ...td, color: h.sc }}>{tx.parentMo || "—"}</td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
        </>;
      })()}
      </>;
    })()}
  </div>;
}

export default TenPayHist;
